import React, { useState, useEffect } from 'react';
import { 
  BrowserRouter as Router, 
  Routes, 
  Route, 
  Navigate, 
  useLocation,
  Link,
  useSearchParams
} from 'react-router-dom';
import { auth, onAuthStateChanged, signOut, db, doc, onSnapshot, collection, query, where, getDocs, limit, orderBy } from './firebase';
import { handleFirestoreError } from './lib/firebaseUtils';
import { ErrorBoundary } from './components/ErrorBoundary';
import LandingPage from './pages/LandingPage';
import Studio from './pages/Studio';
import Success from './pages/Success';
import Inference from './pages/Inference';
import Synthesis from './pages/Synthesis';
import Kinetics from './pages/Kinetics';
import VisualEditor from './pages/VisualEditor';
import DesignSystem from './pages/DesignSystem';
import Onboarding from './pages/Onboarding';
import OutreachTracker from './pages/OutreachTracker';
import Settings from './pages/Settings';
import PublicView from './pages/PublicView';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Gallery from './pages/Gallery';
import Marketplace from './pages/Marketplace';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Documentation from './pages/Documentation';
import About from './pages/About';
import Product from './pages/Product';
import AuthPage from './pages/AuthPage';
import ProductSynthesis from './pages/product/Synthesis';
import ProductKinetics from './pages/product/Kinetics';
import ProductWorkbench from './pages/product/Workbench';
import UseCaseStartups from './pages/use-cases/Startups';
import UseCaseEnterprise from './pages/use-cases/Enterprise';
import UseCaseAgencies from './pages/use-cases/Agencies';
import SolutionGTM from './pages/solutions/GTM';
import SolutionPrototyping from './pages/solutions/Prototyping';
import SolutionDeployment from './pages/solutions/Deployment';
import ResourceLearning from './pages/resources/Learning';
import ResourceAPI from './pages/resources/APIReference';
import ResourceBlueprints from './pages/resources/Blueprints';
import CompanyTeam from './pages/company/Team';
import CompanyVision from './pages/company/Vision';
import CompanyCareers from './pages/company/Careers';
import PricingIndividual from './pages/pricing/Individual';
import PricingTeam from './pages/pricing/Team';
import PricingEnterprise from './pages/pricing/Enterprise';

import UseCases from './pages/UseCases';
import Solutions from './pages/Solutions';
import Resources from './pages/Resources';
import Pricing from './pages/Pricing';
import { useParams } from 'react-router-dom';
import UITour from './components/UITour';
import FeedbackButton from './components/FeedbackButton';
import SentimentSurvey from './components/SentimentSurvey';
import { trackEvent } from './lib/analytics';
import { Toaster } from 'sonner';
import { 
  LayoutDashboard, 
  Sparkles, 
  Layers, 
  Zap, 
  Palette, 
  Brush,
  LogOut,
  Menu,
  X,
  Settings as SettingsIcon,
  Store,
  AlertCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

import { YachtLogo } from './components/YachtLogo';
import { TaskProvider, useTasks } from './contexts/TaskContext';
import { parseFiles } from './lib/parser';

const Sidebar = ({ user }: { user: any }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(true);

  const navItems = [
    { name: 'Dashboard', path: '/studio', icon: LayoutDashboard },
    { name: 'Think', path: '/studio/think', icon: Sparkles },
    { name: 'Build', path: '/studio/build', icon: Layers },
    { name: 'Ship', path: '/studio/ship', icon: Zap },
    { name: 'Edit', path: '/studio/edit', icon: Palette },
    { name: 'Design', path: '/studio/design', icon: Brush },
    { name: 'Marketplace', path: '/studio/marketplace', icon: Store },
    { name: 'Settings', path: '/studio/settings', icon: SettingsIcon },
  ];

  return (
    <div 
      id="sidebar-nav"
      className={`h-screen sticky top-0 bg-[#050505] border-r border-[#222222] flex flex-col transition-all duration-300 overflow-y-auto shrink-0 ${isOpen ? 'w-64' : 'w-20'}`}
    >
      <div className="p-6 flex items-center justify-between border-b border-[#222222]">
        {isOpen && (
          <div className="flex items-center gap-3">
            <YachtLogo className="w-5 h-5" />
            <span className="font-mono text-xl tracking-tighter uppercase text-white">Yacht Labs</span>
          </div>
        )}
        <button onClick={() => setIsOpen(!isOpen)} className="text-[#888888] hover:text-white transition-colors">
          {isOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <nav className="flex-1 px-4 space-y-1 mt-6">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const projectId = new URLSearchParams(location.search).get('project');
          const pathWithProject = projectId ? `${item.path}?project=${projectId}` : item.path;
          
          return (
            <Link
              key={item.path}
              to={pathWithProject}
              className={`flex items-center p-3 rounded-lg transition-colors ${
                isActive ? 'bg-[#111111] text-white border border-[#333333]' : 'text-[#888888] hover:bg-[#111111] hover:text-white border border-transparent'
              }`}
            >
              <item.icon size={18} className={isActive ? 'text-[#0074D9]' : ''} />
              {isOpen && <span className="ml-4 font-mono text-xs uppercase tracking-widest font-semibold">{item.name}</span>}
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-[#222222] bg-[#0A0A0A]">
        <div className="flex items-center mb-4 px-2">
          <img src={user?.photoURL || ''} alt="" className="w-8 h-8 rounded-full border border-[#333333]" />
          {isOpen && (
            <div className="ml-3 overflow-hidden">
              <p className="font-mono text-xs text-white truncate">{user?.displayName}</p>
              <p className="font-mono text-[10px] text-[#888888] truncate">{user?.email}</p>
            </div>
          )}
        </div>
        <button 
          onClick={() => signOut(auth)}
          className="w-full flex items-center p-3 text-[#888888] hover:text-[#FF4444] hover:bg-[#FF4444]/10 rounded-lg transition-colors border border-transparent hover:border-[#FF4444]/20"
        >
          <LogOut size={18} />
          {isOpen && <span className="ml-4 font-mono text-xs uppercase tracking-widest font-semibold">Sign Out</span>}
        </button>
      </div>
    </div>
  );
};

const ThreadRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/studio/outreach?lead=${id}`} replace />;
};

export default function App() {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [pendingSurvey, setPendingSurvey] = useState<{ id: string, projectId: string } | null>(null);
  const [isQuotaExceeded, setIsQuotaExceeded] = useState(false);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (!u) {
        setProfile(null);
        setLoading(false);
      }
    });
    return unsubscribeAuth;
  }, []);

  // Check for pending surveys (deployments > 24h old)
  useEffect(() => {
    if (!user) return;

    const checkSurveys = async () => {
      try {
        console.log('[Survey] Checking for pending surveys...');
        const q = query(
          collection(db, 'deployments'),
          where('ownerId', '==', user.uid),
          where('surveyCompleted', '==', false),
          limit(10) // Optimization: don't fetch all, just a few candidates
        );
        
        const querySnapshot = await getDocs(q);
        const now = new Date().getTime();
        const oneDayInMs = 24 * 60 * 60 * 1000;

        const overdueDeployment = querySnapshot.docs.find(doc => {
          const data = doc.data();
          const deployedAt = data.deployedAt?.seconds * 1000;
          // Only show survey if it's been 24 hours since deployment
          return deployedAt && (now - deployedAt) > oneDayInMs;
        });

        if (overdueDeployment) {
          console.log('[Survey] Found overdue deployment:', overdueDeployment.id);
          setPendingSurvey({
            id: overdueDeployment.id,
            projectId: overdueDeployment.data().projectId
          });
        }
      } catch (error: any) {
        console.error("Error checking surveys:", error);
        if (error?.message?.includes('resource-exhausted') || error?.message?.includes('quota-exceeded')) {
          console.warn('[Quota] Survey check failed due to exhaustion. Skipping.');
          setIsQuotaExceeded(true);
        }
      }
    };

    checkSurveys();
  }, [user]);

  useEffect(() => {
    if (user) {
      const unsubscribeProfile = onSnapshot(doc(db, 'users', user.uid), (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setProfile({
            id: snap.id,
            plan: 'free',
            generationsUsed: 0,
            ...data
          });
        } else {
          setProfile({ onboarded: false, plan: 'free', generationsUsed: 0 });
        }
        setLoading(false);
      }, (error: any) => {
        console.error("Profile sync failed:", error);
        if (error?.message?.includes('resource-exhausted') || error?.message?.includes('quota-exceeded')) {
          setIsQuotaExceeded(true);
        }
        setLoading(false);
      });

      // Track project count for pricing gating
      const qProjects = query(collection(db, 'projects'), where('ownerId', '==', user.uid));
      const unsubscribeProjects = onSnapshot(qProjects, (snap) => {
        setProfile((prev: any) => ({
          ...prev,
          projectCount: snap.size
        }));
      });

      return () => {
        unsubscribeProfile();
        unsubscribeProjects();
      };
    }
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <motion.div 
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-2 border-zinc-800 border-t-zinc-100 rounded-full"
        />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      {isQuotaExceeded && (
        <div className="bg-red-900/90 text-white px-4 py-2 text-[10px] font-mono uppercase tracking-[0.2em] flex items-center justify-center gap-4 text-center sticky top-0 z-[9999] backdrop-blur-md border-b border-red-500/30">
          <AlertCircle size={14} className="text-red-400" />
          <span>System Capacity Reached: Daily Firestore Quota Exhausted. Updates may be delayed until midnight UTC.</span>
          <AlertCircle size={14} className="text-red-400" />
        </div>
      )}
      <TaskProvider>
        <Router>
          <AppContent 
            user={user} 
            profile={profile} 
            pendingSurvey={pendingSurvey} 
            setPendingSurvey={setPendingSurvey} 
          />
        </Router>
      </TaskProvider>
    </ErrorBoundary>
  );
}

function BackgroundTaskTracker() {
  const { tasks } = useTasks();
  const location = useLocation();

  // If we are already on a page that shows task progress, we might not need this.
  // But for "background" awareness, showing a small floating indicator if navigating away.
  const activeTasks = Object.values(tasks).filter(t => t.status === 'running');
  
  if (activeTasks.length === 0) return null;

  return (
    <div className="fixed bottom-24 left-8 z-50 flex flex-col gap-2">
      {activeTasks.map(task => (
        <motion.div 
          key={task.id}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-black/80 backdrop-blur-md border border-[#0074D9]/30 rounded-lg p-3 flex items-center gap-4 shadow-2xl min-w-[200px]"
        >
          <div className="relative w-8 h-8 flex items-center justify-center">
             <motion.div 
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 border-2 border-[#0074D9]/20 border-t-[#0074D9] rounded-full"
             />
             <Zap size={14} className="text-[#0074D9]" />
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-white">Synthesizing {task.type}...</p>
            <div className="w-24 h-1 bg-zinc-800 rounded-full mt-1 overflow-hidden">
               <div 
                className="h-full bg-[#0074D9] transition-all duration-500" 
                style={{ width: `${task.progress}%` }} 
               />
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function AppContent({ user, profile, pendingSurvey, setPendingSurvey }: any) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tasks } = useTasks();
  const [lastBuild, setLastBuild] = useState<{ brief: string | null, files: { name: string, content: string }[], docId: string | null, title?: string } | null>(null);

  const projectId = searchParams.get('project');

  // Track page views
  useEffect(() => {
    trackEvent('page_view', { path: location.pathname });
  }, [location.pathname]);

  // Auto-select latest project and build when signed in
  useEffect(() => {
    if (!user || projectId || !profile?.onboarded || !location.pathname.startsWith('/studio')) return;

    const autoSelect = async () => {
      try {
        // 1. Fetch latest project
        const qProjects = query(
          collection(db, 'projects'),
          where('ownerId', '==', user.uid),
          orderBy('updatedAt', 'desc'),
          limit(1)
        );
        const projectsSnap = await getDocs(qProjects);
        
        if (!projectsSnap.empty) {
          const latestProject = projectsSnap.docs[0];
          const latestProjectId = latestProject.id;
          
          // Update URL params
          setSearchParams({ project: latestProjectId });

          // 2. Fetch latest build for this project to pre-populate workbench
          const qBuilds = query(
            collection(db, `projects/${latestProjectId}/documents`),
            orderBy('createdAt', 'desc'),
            limit(1)
          );
          const buildsSnap = await getDocs(qBuilds);
          if (!buildsSnap.empty) {
            const latestBuildDoc = buildsSnap.docs[0];
            const buildData = latestBuildDoc.data();
            const files = parseFiles(buildData.content);
            
            setLastBuild({
              brief: buildData.content,
              files: Object.entries(files).map(([name, content]: [string, any]) => ({ name, content })),
              docId: latestBuildDoc.id,
              title: buildData.title
            });
          }
        }
      } catch (err) {
        console.error("Auto-select failed:", err);
      }
    };

    autoSelect();
  }, [user, projectId, profile?.onboarded, location.pathname]);

  return (
    <>
      <Toaster position="top-right" theme="dark" />
      <FeedbackButton user={user} />
      <BackgroundTaskTracker />
      {pendingSurvey && (
        <SentimentSurvey 
          deploymentId={pendingSurvey.id} 
          projectId={pendingSurvey.projectId} 
          onClose={() => setPendingSurvey(null)} 
        />
      )}
      <Routes>
        <Route path="/" element={user ? <Navigate to="/studio" /> : <LandingPage />} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/signup" element={<AuthPage />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/security" element={<Documentation />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/docs" element={<Documentation />} />
        <Route path="/about" element={<About />} />
        <Route path="/product" element={<Product />} />
        <Route path="/use-cases" element={<UseCases />} />
        <Route path="/solutions" element={<Solutions />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/pricing" element={<Pricing />} />
        
        {/* Subpages */}
        <Route path="/product/synthesis" element={<ProductSynthesis />} />
        <Route path="/product/kinetics" element={<ProductKinetics />} />
        <Route path="/product/workbench" element={<ProductWorkbench />} />
        <Route path="/use-cases/startups" element={<UseCaseStartups />} />
        <Route path="/use-cases/enterprise" element={<UseCaseEnterprise />} />
        <Route path="/use-cases/agencies" element={<UseCaseAgencies />} />
        <Route path="/solutions/gtm" element={<SolutionGTM />} />
        <Route path="/solutions/prototyping" element={<SolutionPrototyping />} />
        <Route path="/solutions/deployment" element={<SolutionDeployment />} />
        <Route path="/resources/learning" element={<ResourceLearning />} />
        <Route path="/resources/api-reference" element={<ResourceAPI />} />
        <Route path="/resources/blueprints" element={<ResourceBlueprints />} />
        <Route path="/company/team" element={<CompanyTeam />} />
        <Route path="/company/vision" element={<CompanyVision />} />
        <Route path="/company/careers" element={<CompanyCareers />} />
        <Route path="/pricing/individual" element={<PricingIndividual />} />
        <Route path="/pricing/team" element={<PricingTeam />} />
        <Route path="/pricing/enterprise" element={<PricingEnterprise />} />

        <Route path="/pub/:id" element={<PublicView />} />
        <Route path="/thread/:id" element={<ThreadRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
        <Route 
          path="/studio/*" 
          element={
            user ? (
              profile?.onboarded ? (
                <div className="flex bg-[#050505] min-h-screen">
                  <Sidebar user={user} />
                  <main id="main-canvas" className="flex-1 overflow-auto relative bg-[#050505]">
                    <UITour />
                    <Routes>
                      <Route index element={<Studio profile={profile} />} />
                      <Route path="success" element={<Success />} />
                      <Route path="think" element={<Inference profile={profile} lastBuild={lastBuild} setLastBuild={setLastBuild} />} />
                      <Route path="build" element={<Synthesis profile={profile} lastBuild={lastBuild} setLastBuild={setLastBuild} />} />
                      <Route path="ship" element={<Kinetics profile={profile} lastBuild={lastBuild} setLastBuild={setLastBuild} />} />
                      <Route path="edit" element={<VisualEditor profile={profile} lastBuild={lastBuild} setLastBuild={setLastBuild} />} />
                      <Route path="outreach" element={<OutreachTracker profile={profile} />} />
                      <Route path="design" element={<DesignSystem profile={profile} lastBuild={lastBuild} setLastBuild={setLastBuild} />} />
                      <Route path="marketplace" element={<Marketplace profile={profile} setLastBuild={setLastBuild} />} />
                      <Route path="settings" element={<Settings profile={profile} />} />
                    </Routes>
                  </main>
                </div>
              ) : (
                <Onboarding />
              )
            ) : (
              <Navigate to="/" />
            )
          } 
        />
      </Routes>
    </>
  );
}
